/**
 * Playing a recording while it records (1.0.0-rc.1). The editor plays the snapshot playlist of a live proxy (the
 * segments recorded so far, closed with #EXT-X-ENDLIST): an ordinary video of that length, which seeks anywhere,
 * so the source viewer, the sequence viewer's media clock, Web Audio, gain and fades all work as with an MP4 proxy.
 *
 * Safari plays it with its own HLS player (src = the playlist); other browsers through hls.js, loaded on first use.
 * As the recording grows, `refresh` loads the snapshot again where the element is, paused or playing as it was.
 *
 * 1.9.1: `follow` plays the open EVENT playlist instead (the source viewer's Go live): hls.js (and Safari) read it
 * again as it grows and keep playing at the edge with no reload. `setUrl` takes up a newer signed link without
 * reloading: hls.js's later requests (playlist and segments) use its token.
 */
import type HlsType from 'hls.js';

/** Safari (macOS, iPhone, iPad) has its own HLS player, which PRISM uses there. */
export function usesNativeHls(el: HTMLMediaElement): boolean {
  return typeof navigator !== 'undefined' && /Apple/.test(navigator.vendor) && el.canPlayType('application/vnd.apple.mpegurl') !== '';
}

let hlsModule: Promise<typeof HlsType> | null = null;
const loadHls = () => (hlsModule ??= import('hls.js').then((m) => m.default));

const MEDIA_TOKEN = /\/media\/([^/?#]+)\//;

/**
 * A request for a signed live file with the token of `current` (a newer link to the same recording), so a long
 * follow keeps working after the first token has expired. Other URLs are left as they are.
 */
export function withTokenOf(requestUrl: string, current: string): string {
  const now = MEDIA_TOKEN.exec(current)?.[1];
  if (!now || !MEDIA_TOKEN.test(requestUrl)) return requestUrl;
  return requestUrl.replace(MEDIA_TOKEN, `/media/${now}/`);
}

export interface HlsAttachment {
  /** Load the snapshot again (it has grown), keeping the position and play state. */
  refresh(): void;
  /** A newer signed link to the same playlist: later requests use its token (hls.js; Safari keeps the first). */
  setUrl(url: string): void;
  /** Whether this attachment follows the open playlist. */
  readonly follow: boolean;
  destroy(): void;
}

/** Play `url` (a snapshot playlist, or with `follow` the open one) in `el`, from `startAt`, playing when `play`. */
export function attachHls(el: HTMLMediaElement, url: string, opts: { onError?: (message: string) => void; follow?: boolean; startAt?: number | null; play?: boolean } = {}): HlsAttachment {
  let destroyed = false;
  let hls: HlsType | null = null;
  let current = url;
  const start = (at: number | null, play: boolean) => {
    if (destroyed) return;
    const resume = () => {
      if (at != null && at > 0) el.currentTime = at;
      if (play) void el.play().catch(() => undefined);
    };
    if (usesNativeHls(el)) {
      el.addEventListener('loadedmetadata', resume, { once: true });
      el.src = current;
      el.load();
      return;
    }
    void loadHls().then((Hls) => {
      if (destroyed) return;
      if (!Hls.isSupported()) { opts.onError?.('This browser cannot play the recording.'); return; }
      let h: HlsType;
      try {
        h = new Hls({
          startPosition: at ?? 0, maxBufferLength: 20, backBufferLength: 30, enableWorker: true,
          // The open playlist: a duration that grows with the playlist (not Infinity), so the timeline and seeking
          // stay as with the snapshot; hls.js's default never jumps to the edge by itself (liveMaxLatencyDurationCount
          // Infinity; setting it would need liveSyncDurationCount too, or the config is refused).
          liveDurationInfinity: false,
          // Every request with the newest token (setUrl).
          xhrSetup: (xhr, reqUrl) => { xhr.open('GET', withTokenOf(reqUrl, current), true); },
        });
      } catch (err) {
        opts.onError?.(`The recording could not be played: ${(err as Error).message}`);
        return;
      }
      hls = h;
      let recovered = 0;
      h.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (recovered++ < 3 && data.type === Hls.ErrorTypes.NETWORK_ERROR) h.startLoad();
        else if (recovered < 4 && data.type === Hls.ErrorTypes.MEDIA_ERROR) h.recoverMediaError();
        else opts.onError?.('The recording could not be loaded.');
      });
      if (play) el.addEventListener('loadedmetadata', () => void el.play().catch(() => undefined), { once: true });
      h.loadSource(current);
      h.attachMedia(el);
    });
  };
  start(opts.startAt ?? null, Boolean(opts.play));
  return {
    follow: Boolean(opts.follow),
    refresh() {
      if (destroyed) return;
      const at = el.currentTime;
      const play = !el.paused;
      hls?.destroy();
      hls = null;
      start(at, play);
    },
    setUrl(next) { current = next; },
    destroy() {
      destroyed = true;
      if (hls) { hls.destroy(); hls = null; }
      else if (usesNativeHls(el)) { el.removeAttribute('src'); el.load(); }
    },
  };
}
